import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import {
  MsalAuthenticationTemplate,
  MsalProvider,
  useMsal,
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import {
  AuthContext,
  AuthContextInterface,
} from "@/shared/lib/authorization/auth-context";
import { backendApi } from "@/services/backend/api";
import { useDispatch } from "react-redux";
import { msalInstance } from "@/shared/lib/authorization/msal-instance";
import { acquireBackendAccessToken } from "@/shared/lib/authorization/msal-access-token";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/react";
import { FrontendUser } from "./frontendUser";
import t from "../../../lang/lang";

export function MsalAuthContextProvider(props: PropsWithChildren) {
  const { children } = props;

  const loginRequest = {};

  return (
    <MsalProvider instance={msalInstance}>
      {/*
        This assumes that there are no public routes in the app and that
        the full application / everything wrapped in the AuthProvider
        should be protected at any time.
        A more decoupled design would use the MsalAuthenticationTemplate
        in an AuthGuard component. This component could then be used as
        needed throughout the application.
      */}
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={loginRequest}
        errorComponent={errorComponent}
        loadingComponent={loadingComponent}
      >
        <MsalAuthContextBuilder>{children}</MsalAuthContextBuilder>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

function MsalAuthContextBuilder({ children }: PropsWithChildren) {
  const dispatch = useDispatch();

  const user = useMsalUser();

  // This is needed to pre-fill the user feedback
  // form of Sentry with the name of the user.
  useEffect(() => {
    Sentry.setUser({ fullName: user?.fullname, email: user?.email });
  }, [user?.email, user?.fullname]);

  const msalAuthContext: AuthContextInterface = useMemo(
    () => ({
      user,
      // there is nothing to be done here,
      // since login state is managed by the MsalProvider
      login: () => {},
      logout: async () => {
        // this clears the state of all backendApi slices
        // and avoids that any state could still be in cache
        // when a new user logs in on the same device
        dispatch(backendApi.util?.resetApiState());
        // this will log out the user and redirect to the
        // page specified in the postLogoutRedirectUri
        // in the msalConfig in msal-instance.ts
        await msalInstance.logoutRedirect();
      },
    }),
    [dispatch, user],
  );

  return (
    <AuthContext.Provider value={msalAuthContext}>
      {children}
    </AuthContext.Provider>
  );
}

function useMsalUser(): FrontendUser | null {
  const { accounts } = useMsal();
  const account = accounts[0] ?? null;

  const [roles, setRoles] = useState<string[]>([]);

  useEffect(() => {
    if (!account) {
      return;
    }
    // Strictly speaking, it is not recommended to decode the
    // access token on the client side. See:
    // https://learn.microsoft.com/en-us/entra/identity-platform/access-tokens
    // However, we do require the roles of the Backend API
    // in the frontend here. The alternative would be an
    // endpoint in the Backend API that returns the roles.
    // This seemed excessive for the current use case.
    // (Consider that the roles are now coupled to the backend API,
    // should another API be added with different roles, a more
    // advanced solution would be needed.)
    acquireBackendAccessToken().then((accessToken) => {
      const decodedToken = jwtDecode(accessToken);
      if ("roles" in decodedToken) {
        setRoles(decodedToken.roles as string[]);
      }
    });
  }, [account]);

  if (!account) {
    return null;
  }

  // This should only happen if MS Entra is wrongly configured.
  if (!account.idTokenClaims) {
    return null;
  }

  return {
    id: account.idTokenClaims.oid ?? "",
    fullname: account.idTokenClaims.name ?? "",
    roles,
    initials: initials(account.idTokenClaims.name ?? ""),
    // The preferred username is not always the email,
    // but is by default and should therefore be suitable
    // for most cases.
    email: account.idTokenClaims.preferred_username ?? "",
  };
}

// initials will attempt to build
function initials(fullname: string): string {
  const names = fullname.split(" ");
  if (names.length === 0) {
    return "";
  }
  const first = names[0].substring(0, 1).toUpperCase();
  if (names.length === 1) {
    return first;
  }
  const second = names[names.length - 1].substring(0, 1).toUpperCase();

  return first + second;
}

function loadingComponent() {
  return <LoadingMsalLogin />;
}

function LoadingMsalLogin() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <output>
        <svg
          aria-hidden="true"
          className="mr-2 inline h-8 w-8 animate-spin fill-green-500 text-gray-200 dark:text-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </output>
      <span className="ml-2">{t("Lade Login")}</span>
    </div>
  );
}

function errorComponent(error: unknown) {
  return <MsalError error={error} />;
}

function MsalError(props: { error: unknown }) {
  const { error } = props;
  if (error != null) {
    return null;
  }

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <span className="">{t("Fehler beim Login")}</span>
    </div>
  );
}
